import { getCategories } from "../fake-server/endpoints/categories";
import {
    getDiscountedProducts,
    getLatestProducts,
    getRelatedProducts,
    getTopRatedProducts,
} from "../fake-server/endpoints/products";
import api from "./api";
import store from "../store";
import { logoutUser } from "../store/user";

const shopApi = {
    /**
     * Returns array of categories.
     *
     * @param {object?} options
     * @param {number?} options.depth
     *
     * @return {Promise<Array<object>>}
     */
    getCategories: (options = {}) => {
        /**
         * This is what your API endpoint might look like:
         *
         * https://example.com/api/categories.json?depth=2
         *
         * where:
         * - 2 = options.depth
         */
        // return fetch(`https://example.com/api/categories.json?${qs.stringify(options)}`)
        //     .then((response) => response.json());

        // This is for demonstration purposes only. Remove it and use the code above.
        return getCategories(options);
    },
    /**
     * Returns category by slug.
     *
     * @param {string} slug
     * @param {object?} options
     * @param {number?} options.depth
     *
     * @return {Promise<object>}
     */
    getCategoryBySlug: async (slug, options = {}) => {
        try {
            const response = await api.get(`categories/${slug}`);
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("Login error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }

        // This is for demonstration purposes only. Remove it and use the code above.
        // return getCategoryBySlug(slug, options);
    },
    /**
     * Returns product.
     *
     * @param {string} id
     * @param {string} locale
     * @return {Promise<object>}
     */
    getProductBySlug: async (id, locale = "en") => {
        const queryParams = new URLSearchParams();
        queryParams.append("lang", locale); // Ajout de la langue dans les paramètres de requête

        const response = await api.get(`products/${id}?${queryParams.toString()}`); // ID dans le chemin, langue dans les query params
        return response.data.product;
    },
    /**
     * Returns array of related products.
     *
     * @param {string}  slug
     * @param {object?} options
     * @param {number?} options.limit
     *
     * @return {Promise<Array<object>>}
     */
    getRelatedProducts: (slug, options = {}) => {
        /**
         * This is what your API endpoint might look like:
         *
         * https://example.com/api/shop/products/screwdriver-a2017/related.json&limit=3
         *
         * where:
         * - screwdriver-a2017 = slug
         * - limit             = options.limit
         */
        // return fetch(`https://example.com/api/products/${slug}/related.json?${qs.stringify(options)}`)
        //     .then((response) => response.json());

        // This is for demonstration purposes only. Remove it and use the code above.
        return getRelatedProducts(slug, options);
    },
    /**
     * Return products list.
     *
     * @param {object?} options
     * @param {number?} options.page
     * @param {number?} options.limit
     * @param {string?} options.sort
     * @param {Object.<string, string>?} filters
     * @param {string} categorySlug
     *
     * @return {Promise<object>}
     */
    getProductsList: async (options = {}, filters = {}, categorySlug, locale = "en") => {
        try {
            // Initialisez les paramètres de la requête
            const queryParams = new URLSearchParams();

            // Ajoutez les filtres et options dynamiquement
            if (categorySlug) queryParams.append("category", categorySlug);
            if (filters.price) queryParams.append("price", filters.price);
            if (filters.category) queryParams.append("category", filters.category);
            if (filters.brand) queryParams.append("brand", filters.brand);

            if (options.page) queryParams.append("page", options.page);
            if (options.limit) queryParams.append("limit", options.limit);
            if (options.sort) queryParams.append("sort", options.sort);

            // Ajoutez la langue dans la requête
            queryParams.append("lang", locale);

            // Effectuez la requête avec les paramètres construits
            const response = await api.get(`products?${queryParams.toString()}`);

            return response.data; // Retourne les données du produit
        } catch (error) {
            console.error("Error fetching product list:", error);
            throw error; // Rethrow pour gestion des erreurs en aval
        }
    },

    addProduct: async (productData) => {
        try {
            const response = await api.post(`products`, productData);
            console.log(response, `product cree`);
        } catch (err) {
            console.error("Error clearing wishlist:", err);
        }
    },

    /**
     * Returns array of featured products.
     *
     * @param {object?} options
     * @param {number?} options.limit
     * @param {string?} options.category
     *
     * @return {Promise<Array<object>>}
     */
    getFeaturedProducts: async (options = {}, locale = "en") => {
        try {
            const queryParams = new URLSearchParams();
            if (options.limit) queryParams.append("limit", options.limit);
            queryParams.append("lang", locale);
            const response = await api.get(`products/get/featured?${queryParams.toString()}`);
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },
    /**
     * Returns array of latest products.
     *
     * @param {object?} options
     * @param {number?} options.limit
     * @param {string?} options.category
     *
     * @return {Promise<Array<object>>}
     */
    getLatestProducts: (options = {}) => {
        /**
         * This is what your API endpoint might look like:
         *
         * https://example.com/api/shop/latest-products.json?limit=3&category=power-tools
         *
         * where:
         * - 3           = options.limit
         * - power-tools = options.category
         */
        // return fetch(`https://example.com/api/latest-products.json?${qs.stringify(options)}`)
        //     .then((response) => response.json());

        // This is for demonstration purposes only. Remove it and use the code above.
        return getLatestProducts(options);
    },
    /**
     * Returns an array of top rated products.
     *
     * @param {object?} options
     * @param {number?} options.limit
     * @param {string?} options.category
     *
     * @return {Promise<Array<object>>}
     */
    getTopRatedProducts: (options = {}) => {
        /**
         * This is what your API endpoint might look like:
         *
         * https://example.com/api/shop/top-rated-products.json?limit=3&category=power-tools
         *
         * where:
         * - 3           = options.limit
         * - power-tools = options.category
         */
        // return fetch(`https://example.com/api/top-rated-products.json?${qs.stringify(options)}`)
        //     .then((response) => response.json());

        // This is for demonstration purposes only. Remove it and use the code above.
        return getTopRatedProducts(options);
    },
    /**
     * Returns an array of discounted products.
     *
     * @param {object?} options
     * @param {number?} options.limit
     * @param {string?} options.category
     *
     * @return {Promise<Array<object>>}
     */
    getDiscountedProducts: (options = {}) => {
        /**
         * This is what your API endpoint might look like:
         *
         * https://example.com/api/shop/discounted-products.json?limit=3&category=power-tools
         *
         * where:
         * - 3           = options.limit
         * - power-tools = options.category
         */
        // return fetch(`https://example.com/api/discounted-products.json?${qs.stringify(options)}`)
        //     .then((response) => response.json());

        // This is for demonstration purposes only. Remove it and use the code above.
        return getDiscountedProducts(options);
    },
    /**
     * Returns an array of most popular products.
     *

     * @param {number?} limit
  
     * @return {Promise<Array<object>>}
     */
    geBestsellersProducts: async ({ limit }, locale = "en") => {
        try {
            const queryParams = new URLSearchParams();
            if (limit) queryParams.append("limit", limit);
            queryParams.append("lang", locale);
            const response = await api.get(`products/get/bestSeller?${queryParams.toString()}`);
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },
    /**
     * Returns search suggestions.
     *
     * @param {string}  query
     * @param {object?} options
     * @param {number?} options.limit
     * @param {string?} options.category
     *
     * @return {Promise<Array<object>>}
     */
    getSuggestions: async (query, options = {}, locale) => {
        try {
            const response = await api.post(`products/search`, { search: query, lang: locale });            
            return response.data.product_data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },

    /**
     * Returns search suggestions.
     *
     * @param {string}  email
     * @param {string?} password
     *
     * @return {Promise<Array<object>>}
     */

    login: async (email, password) => {
        try {
            const response = await api.post("users/login", { email, password });
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("Login error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },

    logout: async (refreshToken) => {
        try {
            const response = await api.post("users/logout", { refreshToken }).then(() => {
                store.dispatch(logoutUser());
            });
            return response?.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("Login error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },

    /**
     * Returns search suggestions.
     *
     * @param {string}  firstName
     * @param {string}  lastName
     * @param {string}  companyName
     * @param {string}  country
     * @param {string}  address
     * @param {string}  streetAddress
     * @param {string}  city
     * @param {string}  state
     * @param {string}  postcode
     * @param {string}  phone
     * @param {string}  Password
     * @param {string}  email

     *
     * @return {Promise<Array<object>>}
     */
    register: async ({
        firstName,
        lastName,
        streetAddress,
        email,
        phone,
        password,
    }) => {
        try {
            const response = await api.post("users/register", {
                firstName,
                lastName,
                streetAddress,
                email,
                phone,
                password,
                isAdmin: false,
            });
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("Login error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },

    /**
     * Returns search suggestions.
     *
     * @param {string}  registerEmail
     * @param {string}  registerPassword
     * @param {string}  registerFirstName
     * @param {string}  registerLastName
     * @param {string}  registerPhone
     *
     * @return {Promise<Array<object>>}
     */
    updateUser: async (formData, userId) => {
        try {
            const response = await api.put(`users/${userId}`, {
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                isAdmin: true,
            });
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("Login error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },
    changeUserPassword: async ( newPassword, currentPassword, userId) => {
        try {
            const response = await api.post(`users/change-password`, {
                 newPassword, userId, currentPassword
            });
            return response.response.status; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("password:", error);
            return error; // Rethrow the error after logging it, or handle it accordingly
        }
    },

    forgotPassword: async (email) => {
        try {
            const response = await api.post(`users/forgot-password`, {
                email,
            });
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("Login error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },

    newPassword: async (token, password) => {
        try {
            const response = await api.post(`users/reset-password/${token}`, {
                password,
            });
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("Login error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },

    /**
     * Returns search suggestions.
     *
     * @param {string}  userId
     *
     * @return {Promise<Array<object>>}
     */
    // Function to clear the cart after successful transaction
    clearCart: async (userId) => {
        try {
            const response = await api.post("cart/clear", { userId });
            return response.data;
        } catch (error) {
            console.error("Error clearing cart:", error);
        }
    },

    /**
     * Returns search suggestions.
     *
     * @param {string}  userId
     * @return {Promise<Array<object>>}
     */
    // Function to clear the wishlist after successful transaction
    getUserOrder: async (userId, locale) => {
        try {
            const response = await api.get(`orders/order/latest/${userId}?locale=${locale}`);
            return response.data;
        } catch (err) {
            console.error("Error clearing wishlist:", err);
        }
    },
    getUsersOrders: async (statusFilter, locale) => {
        try {
            const response = await api.get(`orders?lang=${locale}&status=${statusFilter}`);
            return response.data;
        } catch (err) {
            console.error("Error clearing orders:", err);
        }
    },
    updateOrdersStatus: async (orderId, status = "Completed") => {
        try {
            const response = await api.put(`orders/${orderId}`, { status});
            return response.data;
        } catch (err) {
            console.error("Error clearing orders:", err);
        }
    },
    updateProduct: async (formData, productId) => {
        try {
            const response = await api.put(`products/${productId}`, {
                name: formData.name,
                description: formData.description,
                richDescription: formData.richDescription,
                images: formData.images,
                brand: formData.brand,
                price: formData.price,
                category: formData.category,
                countInStock: formData.countInStock,
                rating: formData.rating,
                numReviews: formData.numReviews,
                isFeatured: formData.isFeatured,
                parent: formData.parent,
                badges: formData.tags
            });
            return response.data; // Adjust this based on how you want to handle the response
        } catch (error) {
            console.error("Login error:", error);
            throw error; // Rethrow the error after logging it, or handle it accordingly
        }
    },


    /**
     * Returns search suggestions.
     *
     * @param {string}  userId
     * @param {string}  page
     * @return {Promise<Array<object>>}
     */
    // Function to clear the wishlist after successful transaction

    getUserOrders: async (userId, page) => {
        try {
            const response = await api.get(`orders/userorders/${userId}?page=${page}&limit=10`);
            return response.data;
        } catch (err) {
            console.error("Error clearing wishlist:", err);
        }
    },

    /**
     * Returns search suggestions.
     *
     * @param {string}  orderId
     * @return {Promise<Array<object>>}
     */
    // Function to clear the wishlist after successful transaction

    getOrderDetails: async (orderId) => {
        try {
            const response = await api.get(`orders/${orderId}`);
            return response.data;
        } catch (err) {
            console.error("Error clearing userorders:", err);
        }
    },

    sendEmail: async (fomData) => {
        try {
            const response = await api.post('contact', fomData);
            return response.data;
        } catch (err) {
            console.error("Error clearing userorders:", err);
        }
    },
};

export default shopApi;
